import { useState, useMemo, useCallback } from 'react';
import { useApi } from 'hooks/useApi';
import ProductVariantsApi from 'libs/api/productVariants';
import { useTranslation } from 'react-i18next';
import { Images } from 'components/products/actions';
import { useRecoilValue } from 'recoil';
import { getUser } from 'hooks/useUser';
import Helpers from 'helpers';
import { useFilter } from 'hooks/useFilter';
import { Col, Row, Typography } from 'antd';
import useDataExport from '../../data-export/adminView/useDataExport';
import { Link } from 'react-router-dom';
import Button from '../../../components/button';
import { EyeOutlined } from '@ant-design/icons';

const { Paragraph } = Typography;

const api = new ProductVariantsApi();

const rejectedStatuses = ['bad_image', 'bad_info', 'bad_image_info'];

const useArticles = () => {
  const {
    downloadFile,
    downloadTypes,
    loading: productsExportLoading,
  } = useDataExport();
  const { t } = useTranslation();
  const user = useRecoilValue(getUser);
  const { filterData, searchParams } = useFilter();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [additionalFilters, setAdditionalFilters] = useState({});
  const {
    loading,
    data,
    onChangeTableFilter,
    updateInProcess,
    setUpdateInProcess,
    fetchList,
    singleItem,
  } = useApi({
    resourceKey: 'product_variants',
    singleResourceKey: 'product_variant',
    api,
    isMainPage: true,
    additionalFilters,
  });

  const columns = useMemo(
    () => [
      {
        title: t('Azioni'),
        dataIndex: '',
        width: 100,
        render: (data, row) => (
          <Row
            gutter={16}
            justify={'left'}
            onClick={(e) => e.stopPropagation()}
          >
            <Col>
              <Link to={`${row.id}/view`}>
                <Button type={'primary-white'} icon={<EyeOutlined />} />
              </Link>
            </Col>
          </Row>
        ),
      },
      {
        title: t('Insegne'),
        dataIndex: 'shop_title',
        width: 200,
        sorter: () => {},
      },
      {
        title: t('Codice articolo (SKU)'),
        dataIndex: 'product_number',
        width: 200,
        sorter: () => {},
      },
      {
        title: t('Colore'),
        dataIndex: 'main_color',
        width: 200,
        sorter: () => {},
      },
      {
        title: t('Taglia'),
        dataIndex: 'size',
        width: 200,
        sorter: () => {},
      },
      {
        title: t('Nome prodotto'),
        dataIndex: 'title',
        width: 200,
        sorter: () => {},
      },
      {
        title: t('Prodotto in'),
        dataIndex: 'made_in',
        width: 200,
      },
      {
        title: t('Descrizione'),
        dataIndex: 'description',
        width: 200,
        sorter: () => {},
        render: (data) => (
          <Paragraph
            ellipsis={{
              rows: 2,
              expandable: true,
              suffix: '',
            }}
          >
            {Helpers.deleteAllHtmlTags(data)}
          </Paragraph>
        ),
      },
      {
        title: t('Immagine'),
        dataIndex: 'product_variant_images',
        width: 130,
        // sorter: (a, b) => a.image - b.image,
        // sortDirections: ['ascend', 'descend'],
        render: (text, data) => {
          return (
            <Images
              disableModalOpen={rejectedStatuses.includes(data.status)}
              isSuperAdmin={user.isAdmin || user.isOwner}
              handleUploadImages={() => {}}
              handleSetDefaultImage={() => {}}
              handleDeleteImage={() => {}}
              rowData={data}
              images={text}
              setSetCurrentProduct={() => {}}
              t={t}
              handleChangePosition={() => {}}
              handleRotateImage={() => {}}
            />
          );
        },
      },
      {
        title: t('Categoria merceologica'),
        dataIndex: 'category_name_it',
        sorter: () => {},
        width: 200,
        render: (data, row) => {
          return row?.categories_product_variants
            ?.map((i) => i.category.title)
            .join(',');
        },
      },
      {
        title: t('Brand'),
        dataIndex: 'brand_name',
        width: 200,
        sorter: () => {},
      },
      {
        title: t('Stock'),
        dataIndex: 'quantity',
        width: 200,
        sorter: () => {},
      },
      {
        title: t('Ordinato'),
        dataIndex: 'ordered',
        width: 200,
        sorter: () => {},
      },
      {
        title: t('Venduto'),
        dataIndex: 'sold',
        width: 200,
        sorter: () => {},
      },
      {
        title: t('Disponibile'),
        dataIndex: 'quantity_available',
        width: 200,
        sorter: () => {},
      },
      {
        title: t('Prezzo'),
        dataIndex: 'retail_price',
        render: (data, row) => Helpers.numberWithCommas(row.retail_price, ' €'),
        width: 200,
        sorter: () => {},
      },
      {
        title: t('Sconto %'),
        dataIndex: 'discount_percentage',
        width: 200,
        render: (data) => Helpers.numberWithCommas(data, '%'),
        sorter: () => {},
      },
      {
        title: t('Prezzo scontato'),
        dataIndex: 'discounted_price',
        render: (data) => Helpers.numberWithCommas(data, ' €'),
        width: 200,
        sorter: () => {},
      },
      {
        title: t('Wishlist'),
        dataIndex: 'wishing_list_count',
        width: 200,
        sorter: () => {},
      },
      {
        title: t('Dettagli'),
        dataIndex: 'details',
        width: 200,
      },
      {
        title: t('Peso (grammi)'),
        dataIndex: 'product_weight_gram',
        width: 200,
      },
      {
        title: t('Guida alle taglie'),
        dataIndex: 'size_instructions',
        width: 200,
      },
      {
        title: t('Informazioni aggiuntive'),
        dataIndex: 'additional_information',
        width: 200,
      },
      {
        title: t('Stato prodotto'),
        dataIndex: 'status',
        width: 200,
      },
    ],
    [t],
  );

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedKeys(selectedRowKeys);
    },
  };

  const filters = useMemo(() => {
    return [
      {
        title: t('Cerca prodotto'),
        type: 'input',
        name: 'query',
      },
      {
        title: t('Filtro categoria'),
        type: 'select',
        name: 'category_ids',
        isSearch: true,
        values:
          filterData?.categories?.map((i) => ({
            title: i.title,
            value: i.value,
          })) || [],
      },
      {
        title: t('Filtro data caricamento'),
        type: 'range',
        name: 'by_day_from',
        keys: ['created_from', 'created_to'],
      },
    ];
  }, [t, filterData]);

  const handleBulkDestroy = useCallback(async () => {
    setUpdateInProcess(true);
    try {
      await api.bulkDestroy(selectedKeys);

      await Helpers.sleep(5000);

      await fetchList();
      setSelectedKeys([]);
    } catch (e) {
      console.log(e);
    } finally {
      setUpdateInProcess(false);
    }
  }, [selectedKeys]);

  const handleChangeTab = (key) => {
    if (key === 'all') {
      setAdditionalFilters({});
    } else if (key === 'online') {
      setAdditionalFilters({
        is_online: true,
      });
    } else {
      setAdditionalFilters({
        is_online: false,
      });
    }
  };

  const handleExport = useCallback(async () => {
    const currentParams = Object.fromEntries([...searchParams]);
    await downloadFile(
      null,
      { ...currentParams, ...additionalFilters },
      downloadTypes.product,
    );
  }, [searchParams, additionalFilters]);

  return {
    showUploadModal,
    setShowUploadModal,
    data,
    onChangeTableFilter,
    loading,
    columns,
    rowSelection,
    filters,
    selectedKeys,
    updateInProcess,
    handleBulkDestroy,
    handleChangeTab,
    productsExportLoading,
    handleExport,
    singleItem,
  };
};

export default useArticles;
